import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from '@/hooks/useModal';
import { usePaymentStatus } from '../../../hooks/usePaymentStatus';
import { useIntegrations } from '@/queries/useIntegrations';
import { useUser } from '@/queries/useUser';
import { useTaxSettings, useTaxSettingsMutation } from '../../../queries/useTaxSettings';
import { stanNotify } from '@/lib/stanNotify';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import type { TaxAddress, TaxRegistration } from '@/lib/api/settings';

/**
 * Format tax registrations into readable tags
 */
const formatRegistrationTags = (registrations: TaxRegistration[]): string[] => {
    const tags: string[] = [];
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

    registrations.forEach(registration => {
        const countryOptions = registration.country_options[registration.country.toLowerCase()];
        const countryName = regionNames.of(registration.country) || registration.country;

        if (registration.country === 'US') {
            tags.push(`${countryName}: ${countryOptions?.state || ''}`);
        } else if (countryOptions?.type === 'oss_union') {
            tags.push(`${countryName}: OSS`);
        } else if (registration.country === 'CA' && countryOptions?.type === 'province_standard') {
            tags.push(`${countryName}: ${countryOptions?.province_standard?.province || ''}`);
        } else {
            tags.push(countryName);
        }
    });

    return tags;
};

/**
 * Format tax address into a display string
 */
const formatTaxAddressString = (address: TaxAddress): string | null => {
    const parts = [address.street, address.city, address.state, address.postal_code, address.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
};

export const useTaxSectionState = () => {
    const { user } = useUser();
    const { data: userIntegrations } = useIntegrations();
    const hasProducts = (user?.active_product_types?.length ?? 0) > 0;
    const { isStripeConnected } = usePaymentStatus(userIntegrations, hasProducts);

    // Check if user is affiliate-only (affiliates don't have tax settings)
    const isAffiliateOnly = user?.subscription_tier === 'affiliate';

    // Tax settings query - only fetch when Stripe is connected and not affiliate-only
    const { data: taxSettings, isLoading: taxSettingsLoading } = useTaxSettings(isStripeConnected && !isAffiliateOnly);
    const { mutateAsync: updateTaxSettings, isPending: updateLoading } = useTaxSettingsMutation();

    // Local state
    const [chargeStripeTax, setChargeStripeTax] = useState(false);
    const [taxBehavior, setTaxBehavior] = useState<'inclusive' | 'exclusive'>('inclusive');
    const [taxAddress, setTaxAddress] = useState<TaxAddress>({
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
    });

    // Modal state
    const { isOpen: isAddressModalOpen, open: openAddressModal, close: closeAddressModal } = useModal();

    // Initialize state from API data
    useEffect(() => {
        if (taxSettings) {
            setChargeStripeTax(taxSettings.charge_stripe_tax);
            setTaxBehavior(
                (taxSettings.account_settings.defaults.tax_behavior as 'inclusive' | 'exclusive') || 'inclusive'
            );

            const headOffice = taxSettings.account_settings.head_office;
            if (headOffice?.address) {
                setTaxAddress({
                    street: headOffice.address.line1 || '',
                    city: headOffice.address.city || '',
                    state: headOffice.address.state || '',
                    postal_code: headOffice.address.postal_code || '',
                    country: headOffice.address.country || '',
                });
            }
        }
    }, [taxSettings]);

    // Derived state
    const taxAddressString = useMemo(() => formatTaxAddressString(taxAddress), [taxAddress]);
    const hasTaxIssue = taxSettings?.account_settings.status === 'pending';
    const activeRegistrationTags = useMemo(
        () => formatRegistrationTags(taxSettings?.active_registrations || []),
        [taxSettings?.active_registrations]
    );

    // Update settings helper
    const saveSettings = useCallback(
        async (updates: {
            chargeStripeTax?: boolean;
            taxBehavior?: 'inclusive' | 'exclusive';
            address?: TaxAddress;
        }) => {
            const currentAddress = updates.address || taxAddress;
            const currentBehavior = updates.taxBehavior || taxBehavior;
            const currentCharge = updates.chargeStripeTax ?? chargeStripeTax;

            try {
                await updateTaxSettings({
                    tax_behaviour: currentBehavior,
                    head_office_line1: currentAddress.street,
                    head_office_city: currentAddress.city,
                    head_office_state: currentAddress.state,
                    head_office_postal: currentAddress.postal_code,
                    head_office_country: currentAddress.country,
                    charge_stripe_tax: currentCharge,
                });
            } catch (error) {
                handleApiError(error, 'Something went wrong');
                throw error;
            }
        },
        [taxAddress, taxBehavior, chargeStripeTax, updateTaxSettings]
    );

    // Handle tax toggle
    const handleTaxToggle = useCallback(
        async (enabled: boolean) => {
            setChargeStripeTax(enabled);

            // If enabling and no address, open modal
            if (enabled && !taxAddressString) {
                openAddressModal();
                return;
            }

            try {
                await saveSettings({ chargeStripeTax: enabled });
            } catch {
                // Revert on error
                setChargeStripeTax(!enabled);
            }
        },
        [taxAddressString, openAddressModal, saveSettings]
    );

    // Handle tax behavior change
    const handleTaxBehaviorChange = useCallback(
        async (behavior: 'inclusive' | 'exclusive') => {
            const previousBehavior = taxBehavior;
            setTaxBehavior(behavior);

            try {
                await saveSettings({ taxBehavior: behavior });
            } catch {
                // Revert on error
                setTaxBehavior(previousBehavior);
            }
        },
        [taxBehavior, saveSettings]
    );

    // Handle address update from modal
    const handleAddressUpdate = useCallback(
        async (newAddress: TaxAddress) => {
            const previousAddress = taxAddress;
            setTaxAddress(newAddress);

            try {
                await saveSettings({ address: newAddress });
                closeAddressModal();
                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'Tax address has been updated.',
                });
            } catch {
                // Revert on error
                setTaxAddress(previousAddress);
            }
        },
        [taxAddress, saveSettings, closeAddressModal]
    );

    // Handle modal close - if no address was entered, disable taxes
    const handleAddressModalClose = useCallback(() => {
        if (!taxAddressString) {
            setChargeStripeTax(false);
        }
        closeAddressModal();
    }, [taxAddressString, closeAddressModal]);

    return {
        // State
        isStripeConnected,
        taxSettingsLoading,
        updateLoading,
        chargeStripeTax,
        taxBehavior,
        taxAddress,
        taxAddressString,
        hasTaxIssue,
        activeRegistrationTags,

        // Modal
        isAddressModalOpen,
        openAddressModal,

        // Handlers
        handleTaxToggle,
        handleTaxBehaviorChange,
        handleAddressUpdate,
        handleAddressModalClose,
    };
};
